import { CompositeLayer } from "@deck.gl/core"
import type { CompositeLayerProps, Layer } from "@deck.gl/core"
import { useAppSelector } from "integrations/redux/hooks"
import { useWeatherHeatmapOpacity, selectWeatherTimeline } from "features/map/control/selectors"
import { useMapBackground } from "features/map/control/useMapStyles"
import { RadarHeatmapLayer } from "./RadarHeatmapLayer"
import { RadarCoverageMaskLayer } from "./RadarCoverageMaskLayer"
import { createNoCoverageLayer } from "../shared/NoCoverageLayer"
import { createTileSource } from "../series/weatherTileSource"
import { useDisplayFrame, useWeatherSeries } from "../series/weatherSeries"
import type { FrameImage } from "../shared/frameTextureCache"

const OVERLAY_RGBA_LIGHT: [number, number, number, number] = [140, 140, 140, 60]
const OVERLAY_RGBA_DARK: [number, number, number, number] = [20, 20, 20, 100]

const source = createTileSource("radar")

type RadarCompositeProps = CompositeLayerProps & {
    image: FrameImage
    gridBounds: [number, number, number, number]
    bounds: [number, number, number, number]
    opacity: number
    overlayColor: [number, number, number, number]
}

class RadarCompositeLayer
    extends CompositeLayer<RadarCompositeProps>
    implements Layer<RadarCompositeProps>
{
    static layerName = "RadarLayer"

    renderLayers(): Layer[] {
        const { image, gridBounds, bounds, opacity, overlayColor } = this.props
        return [
            new RadarCoverageMaskLayer({
                id: `${this.props.id}-mask`,
                coverage: image,
                bounds,
                overlayColor,
            }),
            new RadarHeatmapLayer({
                id: `${this.props.id}-heatmap`,
                image,
                bounds: gridBounds,
                opacity,
            }),
        ]
    }
}

export const useRadarLayer = () => {
    const series = useWeatherSeries("radar", source)
    const { currentTime } = useAppSelector(selectWeatherTimeline)
    const opacity = useWeatherHeatmapOpacity()
    const { isLight } = useMapBackground()
    const frame = useDisplayFrame("radar", series, currentTime)

    if (!series) return { layer: null }

    if (!series.hasCoverage) {
        return { layer: createNoCoverageLayer(series.bounds, "RadarNoCoverageLayer") }
    }

    if (!frame) return { layer: null }

    return {
        layer: new RadarCompositeLayer({
            id: "RadarLayer",
            image: frame.imageA,
            gridBounds: frame.bounds,
            bounds: series.bounds,
            opacity,
            overlayColor: isLight ? OVERLAY_RGBA_LIGHT : OVERLAY_RGBA_DARK,
        }),
    }
}
